import { useEffect } from 'react';
import { useAdminModuleAccess } from '../context/AdminModuleAccessContext';

const ALLOW_ZONE = '[data-admin-allow-view-only]';
const PAGINATION_ZONE = '[data-admin-pagination]';
const SORT_ZONE = '[data-admin-sort]';
const ROW_NAV_ZONE = '[data-admin-row-nav]';
const TAB_ZONE = '[data-admin-tab], [role="tab"], [role="tablist"]';

const VIEW_NAV_LABEL =
  /^(view|edit|open|details?|see\s+details?|view\s+details?)$/i;

function getInteractiveTarget(eventTarget) {
  if (!(eventTarget instanceof Element)) return null;
  return eventTarget.closest(
    'button, a, input[type="submit"], input[type="button"], [role="button"]',
  );
}

function isAllowedInteractive(el) {
  if (!el) return true;
  if (el.closest(ALLOW_ZONE)) return true;
  if (el.closest(PAGINATION_ZONE)) return true;
  if (el.closest(SORT_ZONE)) return true;
  if (el.closest(ROW_NAV_ZONE)) return true;
  if (el.closest(TAB_ZONE)) return true;
  return false;
}

function labelOf(el) {
  return (el?.textContent || el?.getAttribute?.('aria-label') || '').replace(/\s+/g, ' ').trim();
}

function isAddNavigation(el) {
  if (!el) return false;
  const href = el.getAttribute?.('href') || '';
  if (/\/add(\/|$)/.test(href) || /\/new(\/|$)/.test(href)) return true;
  const label = labelOf(el);
  return /^(add(\s|$)|create(\s|$)|new(\s|$))/i.test(label);
}

function isRowActionsTrigger(el) {
  if (!el) return false;
  const label = (el.getAttribute('aria-label') || el.getAttribute('title') || '').toLowerCase();
  return label.includes('row actions') || label === 'actions';
}

function isViewNavAction(el) {
  return VIEW_NAV_LABEL.test(labelOf(el));
}

function isSearchOrFilterControl(el) {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.closest(ALLOW_ZONE)) return true;
  const type = (el.getAttribute('type') || '').toLowerCase();
  if (type === 'search') return true;
  const placeholder = (el.getAttribute('placeholder') || '').toLowerCase();
  if (placeholder.includes('search') || placeholder.includes('filter')) return true;
  const name = (el.getAttribute('name') || '').toLowerCase();
  if (name.includes('search') || name.includes('filter')) return true;
  const aria = (el.getAttribute('aria-label') || '').toLowerCase();
  if (aria.includes('search') || aria.includes('filter')) return true;
  return false;
}

function applyFieldRestrictions(root, { restrictMutations }) {
  if (!restrictMutations || !root) return;

  root.querySelectorAll('input, textarea, select').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (isSearchOrFilterControl(el)) return;
    if (el.closest(ALLOW_ZONE)) return;

    const tag = el.tagName;
    const type = (el.getAttribute('type') || '').toLowerCase();

    if (tag === 'SELECT' || type === 'checkbox' || type === 'radio' || type === 'file' || type === 'range') {
      el.setAttribute('disabled', 'true');
      el.setAttribute('data-admin-view-only-disabled', 'true');
      return;
    }

    el.setAttribute('readonly', 'true');
    el.setAttribute('data-admin-view-only-readonly', 'true');
  });

  root.querySelectorAll('[contenteditable="true"]').forEach((el) => {
    if (el.closest(ALLOW_ZONE)) return;
    el.setAttribute('contenteditable', 'false');
    el.setAttribute('data-admin-view-only-readonly', 'true');
  });
}

/**
 * Enforces view-only / no-edit module permissions inside admin content:
 * - Blocks Add CTAs and /add navigation when !canAdd
 * - Blocks mutating CTAs when view-only or !canEdit
 * - Makes form fields read-only when mutations are restricted
 * - Keeps search, filters, sort, pagination, and view/edit navigation working
 */
export default function useAdminViewOnlyGuard(containerRef) {
  const { canAdd, canEdit, isViewOnly, restrictMutations } = useAdminModuleAccess();

  useEffect(() => {
    const root = containerRef?.current;
    if (!root) return undefined;

    const needsGuard = !canAdd || restrictMutations;
    if (!needsGuard) return undefined;

    const runFieldPass = () => applyFieldRestrictions(root, { restrictMutations });
    runFieldPass();

    const observer = new MutationObserver(() => {
      runFieldPass();
    });
    observer.observe(root, { childList: true, subtree: true });

    const onClickCapture = (event) => {
      const target = getInteractiveTarget(event.target);
      if (!target) return;
      if (isAllowedInteractive(target)) return;

      // Table / card row navigation (div[role=button]) — allow opening detail/edit
      if (
        target.getAttribute('role') === 'button' &&
        target.tagName !== 'BUTTON' &&
        target.tagName !== 'A'
      ) {
        return;
      }

      if (!canAdd && isAddNavigation(target)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!restrictMutations) return;

      // Allow opening the ⋮ menu and View/Edit items so detail pages stay reachable
      if (isRowActionsTrigger(target) || isViewNavAction(target)) return;

      // Block other buttons / submit / action links
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.getAttribute('type') === 'submit' ||
        target.getAttribute('role') === 'button'
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const onSubmitCapture = (event) => {
      if (!restrictMutations) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest(ALLOW_ZONE)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    root.addEventListener('click', onClickCapture, true);
    root.addEventListener('submit', onSubmitCapture, true);

    return () => {
      observer.disconnect();
      root.removeEventListener('click', onClickCapture, true);
      root.removeEventListener('submit', onSubmitCapture, true);
    };
  }, [containerRef, canAdd, canEdit, isViewOnly, restrictMutations]);
}
